import { useEffect, useMemo, useState } from "react";
import { collection, getDocs, onSnapshot, query, where } from "firebase/firestore";
import { DATABASE } from "../Config/firebase";
import {
  computeSegmentTotals,
  getDepositApprovedAmount,
  getWithdrawalPaidAmount,
} from "../Utils/fundsPeriodTotals";

export const FUNDS_PERIOD_TYPES = {
  DEPOSIT: "deposit",
  WITHDRAWAL: "withdrawal",
};

const useFundsPeriodTotals = ({ type, startDate, endDate, usersMap: externalUsersMap }) => {
  const [transactions, setTransactions] = useState([]);
  const [internalUsersMap, setInternalUsersMap] = useState({});
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const hasExternalUsers = externalUsersMap && Object.keys(externalUsersMap).length > 0;
  const usersMap = hasExternalUsers ? externalUsersMap : internalUsersMap;

  useEffect(() => {
    if (hasExternalUsers) {
      setLoadingUsers(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingUsers(true);
    getDocs(collection(DATABASE, "users"))
      .then((snap) => {
        if (cancelled) return;
        const map = {};
        snap.docs.forEach((d) => {
          map[d.id] = { doc_id: d.id, ...d.data() };
        });
        setInternalUsersMap(map);
      })
      .catch((err) => console.error("useFundsPeriodTotals: failed to load users", err))
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hasExternalUsers]);

  useEffect(() => {
    const isDeposit = type === FUNDS_PERIOD_TYPES.DEPOSIT;
    const q = query(
      collection(DATABASE, "transection_requests"),
      where("request_type", "==", isDeposit ? 0 : 1),
      where("request_status", "==", isDeposit ? 2 : 5)
    );

    setLoadingTransactions(true);
    const unsub = onSnapshot(
      q,
      (snap) => {
        let docs = snap.docs.map((d) => ({ id: d.id, doc_id: d.id, ...d.data() }));
        if (!isDeposit) {
          docs = docs.filter((d) => d.otp_verify === true);
        }
        setTransactions(docs);
        setLoadingTransactions(false);
      },
      (err) => {
        console.error("useFundsPeriodTotals: snapshot error", err);
        setLoadingTransactions(false);
      }
    );

    return unsub;
  }, [type]);

  const totals = useMemo(() => {
    if (!startDate || !endDate) {
      return {
        individual: { total: 0, count: 0 },
        corporate: { total: 0, count: 0 },
        routing: { total: 0, count: 0 },
      };
    }

    const getAmount =
      type === FUNDS_PERIOD_TYPES.DEPOSIT ? getDepositApprovedAmount : getWithdrawalPaidAmount;

    return computeSegmentTotals(transactions, usersMap, { startDate, endDate, getAmount });
  }, [transactions, usersMap, startDate, endDate, type]);

  return {
    totals,
    loading: loadingTransactions || loadingUsers,
  };
};

export default useFundsPeriodTotals;
